import {BUBBLE, Component, DONE, TOUCH} from "@/event/calendar/dom/Component.ts";
import type {GutterSized} from "@/event/calendar/days/GutterSized.ts";
import type {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {Rect} from "@/event/calendar/Shapes.ts";
import {measure} from "@/canvas/CanvasUtils.ts";
import {GUTTER_FONT_SIZE, GUTTER_PADDING} from "@/event/calendar/Constants.ts";
import {dc} from "@fd/editors/html/plugins/Keymap.ts";
import {SCROLLBAR_DISTANCE, SCROLLBAR_WIDTH} from "@/event/calendar/dom/ScrollComponent.ts";
import {ALL_DAY_EVENT_HEIGHT} from "@/event/calendar/days/DaysWholeBody.ts";

const MIN_HEIGHT = 54;
const NICE_HEIGHT = 105;

interface MoreLess {
	to: "more"|"less"|"no";
	// indicator will be drawn in red color
	warn?: boolean;
	at?: number;	// target size
}

export class DaysWholeHeader extends Component implements GutterSized {
	public gutterWidth:number;

	private bodyHeight:number;
	private bodyScrollY:number;

	private moreShape:Path2D;
	private moreHover:boolean;
	private lastClickedMore: MoreLess|null;

	constructor() {
		super();
		this.moreShape = null;
		this.moreHover = false;
		this.lastClickedMore = null;
	}

	calcMinGutter(dc: DrawingContext):number {
		const fontFamily = dc.style.getPropertyValue("--font");
		dc.ctx.font = GUTTER_FONT_SIZE + "px " + fontFamily;
		const title = dc.env.msg("calendar.wholeDay");
		const m = measure(dc.ctx, title);

		return m.width + 2 * GUTTER_PADDING + 1;	// text + padding + border
	}


	protected drawBackground(dc: DrawingContext, area: Rect) {
		dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-header-bg");
		dc.ctx.fillRect(area.x, area.y, area.width, area.height);
	}

	protected drawContent(dc: DrawingContext, area: Rect) {
		const fontFamily = dc.style.getPropertyValue("--font");
		const headerFg = dc.style.getPropertyValue("--cal-header-fg");
		dc.ctx.font = GUTTER_FONT_SIZE + "px " + fontFamily;
		const title = dc.env.msg("calendar.wholeDay");
		dc.ctx.fillStyle = headerFg;
		dc.ctx.fillText(title, GUTTER_PADDING, 16);

		const moreLess = this.needButton();
		if (moreLess.to !== "no") {
			const isMore = moreLess.to === "more";
			const moreButtonFg = moreLess.warn ? dc.style.getPropertyValue("--red") : dc.style.getPropertyValue("--cal-header-separ");
			const moreButtonBg = dc.style.getPropertyValue("--cal-bg");
			const r = TOUCH ? 13 : 8;

			dc.ctx.fillStyle = this.moreHover ? moreButtonFg: moreButtonBg;
			dc.ctx.strokeStyle = moreButtonFg;

			dc.ctx.beginPath();
			const dotY = this.height - r - 4;
			const dotX = this.width/2;
			dc.ctx.ellipse(dotX, dotY, r, r, 0, 0, 2 * Math.PI);
			dc.ctx.fill();
			dc.ctx.stroke();

			dc.ctx.beginPath();
			if (isMore) {
				dc.ctx.moveTo(dotX - 4, dotY - 2);
				dc.ctx.lineTo(dotX, dotY + 2.5);
				dc.ctx.lineTo(dotX + 4, dotY - 2);
			} else {
				dc.ctx.moveTo(dotX - 4, dotY + 2);
				dc.ctx.lineTo(dotX, dotY - 2.5);
				dc.ctx.lineTo(dotX + 4, dotY + 2);
			}
			if (this.moreHover) dc.ctx.strokeStyle = "white";
			dc.ctx.stroke();

			// Hover area
			this.moreShape = new Path2D();
			this.moreShape.ellipse(dotX, dotY, r+.5, r+.5, 0, 0, 2 * Math.PI);
		} else {
			this.moreShape = null;
		}
	}

	protected drawBorder(dc: DrawingContext, area: Rect) {
		dc.ctx.strokeStyle = dc.style.getPropertyValue("--cal-header-separ");
		dc.ctx.beginPath();
		dc.ctx.moveTo(this.width - 0.5, this.y);
		dc.ctx.lineTo(this.width - 0.5, this.height);
		dc.ctx.stroke();
	}

	/*
		M - minimal       O - optimal      R - required     C - current

		A) R...M...O
			1) C.R.M.O - more to M
			2) R.C.M.O - more to M
			3) R.M.C.O - more to O
			4) R.M.O.C - less to M
		B) M...R...O
			1) C.M.R.O - more to R
			2) M.C.R.O - more to R
			3) M.R.C.O - more to O
			4) M.R.O.C - less to R
		C) M...O...R
			1) C.M.O.R - more to R
			2) M.C.O.R - more to R
			3) M.O.C.R - more to R
			4) M.O.R.C - less to R

	 */
	needButton():MoreLess {
		const r = this.bodyHeight;
		const c = this.height;
		const m = MIN_HEIGHT;
		const o = NICE_HEIGHT;

		if (r < m) {		/* A */
			if (c < m) return { to: "more", at: m };
			if (c < o) return { to: "more", at: o };
			return { to: "less", at: m };
		} else if (r < o) {	/* B */
			if (c < r) return { to: "more", at: r, warn: true };
			if (c < o) return { to: "more", at: o };
			return { to: "less", at: r };
		} else {			/* C */
			if (c < r) return { to: "more", at: r, warn: true };
			if (r === c) return { to: "less", at: m };
			return { to: "less", at: r };
		}
	}

	setRequiredHeight(requiredHeight: number, bodyScrollY: number) {
		this.bodyHeight = requiredHeight;
		this.bodyScrollY = bodyScrollY;
	}


	onPointerMove(e: PointerEvent, x: number, y: number): boolean {
		const ctx = this.root().ctx;
		this.setHoverFlag(this.moreShape && ctx.isPointInPath(this.moreShape, x, y));
		return BUBBLE;
	}

	onPointerLeave(e: PointerEvent, x: number, y: number): boolean {
		super.onPointerLeave(e, x, y);
		this.setHoverFlag(false);
		return BUBBLE;
	}

	setHoverFlag(hover:boolean):void {
		const oldHover = this.moreHover;
		this.moreHover = hover;
		if (oldHover !== hover) this.dirty(false);
	}

	onClick(e: PointerEvent, x: number, y: number): boolean {
		const ctx = this.root().ctx;
		if (this.moreShape && ctx.isPointInPath(this.moreShape, x, y)) {
			this.moreHover = false;
			this.showMore();
			return DONE;
		}
		return BUBBLE;
	}

	showMore() {
		this.lastClickedMore = this.needButton();
		this.fireEvent("split", { size: this.lastClickedMore.at });
	}
}